import React, { useEffect, useState } from 'react';
import {
  Modal,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Platform,
  ActivityIndicator,
  ScrollView
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { colores, fuentes } from '../../styles/variablesCss';
import { useUser } from '../../context/UserContext';
import { BACKEND_URL } from '../../Config/Const';

const EncuestaFinalizar = ({ visible, onClose, onSubmit, onOpen }) => {
  const { mail, codUsu } = useUser();
  const [valoracion, setValoracion] = useState(null);
  const [comentario, setComentario] = useState('');
  const [enviando, setEnviando] = useState(false);
  
  // Estado para controlar responsividad
  const getWindowWidth = () => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      return window.innerWidth;
    }
    return 768;
  };
  
  const [windowWidth, setWindowWidth] = useState(getWindowWidth());
  const isMobile = windowWidth < 768;
  const isSmallMobile = windowWidth < 400;
  
  // Estado para la altura del documento
  const [documentHeight, setDocumentHeight] = useState('100vh');
  
  // Calcular altura total del documento
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined' && visible) {
      const updateHeight = () => {
        const body = document.body;
        const html = document.documentElement;
        const height = Math.max(
          body.scrollHeight,
          body.offsetHeight,
          html.clientHeight,
          html.scrollHeight,
          html.offsetHeight
        );
        setDocumentHeight(`${height}px`);
      };
      
      updateHeight();
      setTimeout(updateHeight, 100);
    }
  }, [visible]);
  
  // Actualizar tamaño al montar y al cambiar
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      setWindowWidth(window.innerWidth);
      
      const handleResize = () => {
        setWindowWidth(window.innerWidth);
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);
  
  // Bloquear scroll del body cuando el modal está visible y scroll automático arriba
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      if (visible) {
        // Guardar posición del scroll antes de bloquear
        const scrollY = window.scrollY;
        
        // Scroll instantáneo hacia arriba (mejor para móviles)
        window.scrollTo(0, 0);
        
        // Bloquear scroll inmediatamente
        document.body.style.position = 'fixed';
        document.body.style.top = '0px';
        document.body.style.left = '0px';
        document.body.style.right = '0px';
        document.body.style.width = '100%';
        document.body.style.height = '100vh';
        document.body.style.overflow = 'hidden';
        
        // Prevenir bounce en iOS y bloquear overscroll
        document.body.style.webkitOverflowScrolling = 'touch';
        document.body.style.overscrollBehavior = 'none';
        document.documentElement.style.overflow = 'hidden';
        document.documentElement.style.overscrollBehavior = 'none';
        
        // Prevenir scroll mediante eventos
        const preventScroll = (e) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        };
        
        const preventTouchMove = (e) => {
          // Permitir scroll dentro del contenido del modal
          const modalContent = e.target.closest('[data-modal-content="true"]');
          if (modalContent) {
            // Permitir scroll dentro del modal
            return true;
          }
          
          // Prevenir scroll en el overlay o body
          if (e.target === document.body || e.target.closest('.modal-overlay')) {
            e.preventDefault();
            return false;
          }
        };
        
        // Agregar listeners para prevenir scroll
        window.addEventListener('scroll', preventScroll, { passive: false });
        window.addEventListener('wheel', preventScroll, { passive: false });
        window.addEventListener('touchmove', preventTouchMove, { passive: false });
        document.addEventListener('touchmove', preventTouchMove, { passive: false });
        
        return () => {
          // Remover event listeners
          window.removeEventListener('scroll', preventScroll);
          window.removeEventListener('wheel', preventScroll);
          window.removeEventListener('touchmove', preventTouchMove);
          document.removeEventListener('touchmove', preventTouchMove);
          
          // Restaurar estilos
          document.body.style.position = '';
          document.body.style.top = '';
          document.body.style.left = '';
          document.body.style.right = '';
          document.body.style.width = '';
          document.body.style.height = '';
          document.body.style.overflow = '';
          document.body.style.webkitOverflowScrolling = '';
          document.body.style.overscrollBehavior = '';
          document.documentElement.style.overflow = '';
          document.documentElement.style.overscrollBehavior = '';
          
          // Volver a la posición original del scroll
          window.scrollTo(0, scrollY);
        };
      }
    }
  }, [visible]);
  
  // Notificar al padre cuando el modal se muestra para permitir ocultar loaders
  useEffect(() => {
    if (visible && typeof onOpen === 'function') {
      onOpen();
    }
  }, [visible, onOpen]);
  
  // Opciones de valoración para mantenerlas en un solo lugar
  const opcionesValoracion = [
    { id: 'triste', icon: 'emoticon-sad-outline', texto: 'Mala', color: '#E53935' },
    { id: 'neutral', icon: 'emoticon-neutral-outline', texto: 'Normal', color: '#FFC107' },
    { id: 'contenta', icon: 'emoticon-happy-outline', texto: 'Excelente', color: '#4CAF50' }
  ];

  // Mapear valoración a número (1, 2 o 3)
  const obtenerNota = (valoracion) => {
    const mapeo = {
      'triste': 1,
      'neutral': 2,
      'contenta': 3
    };
    return mapeo[valoracion] || 2;
  };

  // Estilos dinámicos basados en breakpoints
  const getDynamicStyles = () => ({
    modalContainer: {
      ...(Platform.OS === 'web' ? {
        minHeight: documentHeight,
        justifyContent: 'center',
        paddingTop: isMobile ? 0 : 0,
        paddingBottom: isMobile ? 0 : 0,
      } : {
        justifyContent: 'center',
      }),
    },
    modalContent: {
      width: isSmallMobile ? '94%' : isMobile ? '92%' : '90%',
      maxWidth: isSmallMobile ? 380 : isMobile ? 420 : 600,
      padding: isSmallMobile ? 14 : isMobile ? 12 : 20,
      maxHeight: isMobile ? '92dvh' : '88vh',
      ...(Platform.OS === 'web' ? {
        display: 'flex',
      } : {}),
      ...(isMobile ? {
        overflowY: 'hidden',
        overflowX: 'hidden',
        marginTop: 0,
      } : {
        overflowY: 'hidden',
      }),
    },
    scrollWrapper: {
      width: '100%',
    },
    scrollContent: {
      width: '100%',
    },
    scrollContentMobile: {
      paddingBottom: isSmallMobile ? 20 : 24,
    },
    scrollContentDesktop: {
      paddingBottom: 32,
    },
    successCircle: {
      width: isSmallMobile ? 48 : isMobile ? 56 : 72,
      height: isSmallMobile ? 48 : isMobile ? 56 : 72,
      borderRadius: isSmallMobile ? 24 : isMobile ? 28 : 36,
    },
    successIcon: {
      fontSize: isSmallMobile ? 26 : isMobile ? 30 : 38,
    },
    successTitle: {
      fontSize: isSmallMobile ? 24 : isMobile ? 22 : 30,
      marginTop: isSmallMobile ? 12 : isMobile ? 14 : 18,
    },
    successSubtitle: {
      fontSize: isSmallMobile ? 12 : isMobile ? 10 : 14,
      marginTop: isSmallMobile ? 7 : isMobile ? 6 : 9,
    },
    successAuxText: {
      fontSize: isSmallMobile ? 12 : isMobile ? 13 : 14,
      marginTop: isSmallMobile ? 4 : isMobile ? 5 : 7,
    },
    divider: {
      marginVertical: isMobile ? 8 : 12,
    },
    surveyTitle: {
      fontSize: isMobile ? 18 : 20,
    },
    surveySubtitle: {
      fontSize: isMobile ? 14 : 15,
    },
    ratingCard: {
      flex: 1,
      padding: isSmallMobile ? 4 : isMobile ? 6 : 8,
      marginHorizontal: 0,
      minHeight: isSmallMobile ? 78 : isMobile ? 82 : 100,
      minWidth: 0,
    },
    ratingIcon: {
      fontSize: isSmallMobile ? 24 : isMobile ? 26 : 36,
    },
    ratingText: {
      fontSize: isSmallMobile ? 10 : isMobile ? 11 : 14,
      marginTop: isSmallMobile ? 4 : isMobile ? 5 : 8,
    },
    commentLabel: {
      fontSize: isSmallMobile ? 14 : isMobile ? 15 : 16,
    },
    commentInput: {
      minHeight: isSmallMobile ? 76 : isMobile ? 80 : 110,
      padding: isSmallMobile ? 10 : isMobile ? 12 : 16,
      fontSize: isMobile ? 14 : 15,
      borderRadius: isSmallMobile ? 10 : isMobile ? 11 : 12,
    },
    submitButton: {
      paddingVertical: isSmallMobile ? 12 : isMobile ? 12 : 16,
      marginTop: isSmallMobile ? 16 : isMobile ? 18 : 24,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: isSmallMobile ? 46 : isMobile ? 48 : 52,
    },
    submitButtonText: {
      fontSize: isSmallMobile ? 15 : isMobile ? 15 : 16,
      marginLeft: 8,
    },
    skipButton: {
      paddingVertical: isSmallMobile ? 12 : isMobile ? 14 : 16,
      marginTop: isSmallMobile ? 10 : isMobile ? 12 : 16,
    },
    skipButtonText: {
      fontSize: isSmallMobile ? 13 : isMobile ? 14 : 15,
    },
    caritasContainer: {
      marginVertical: isMobile ? 10 : 20,
    },
  });

  // Manejar el envío de la encuesta
  const handleSubmit = async () => {
    if (!valoracion || enviando) return;
    
    setEnviando(true);
    
    try {
      // Preparar los datos para enviar
      const nota = obtenerNota(valoracion);
      const observaciones = comentario.trim() || '';
      
      // Construir query params
      const params = new URLSearchParams();
      if (mail) params.append('mail', mail);
      if (codUsu) params.append('codUsu', codUsu);
      params.append('nota', nota.toString());
      params.append('observaciones', observaciones);
      
      const url = `${BACKEND_URL}/rest/fin/encuesta?${params.toString()}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        
        const datosEncuesta = {
          valoracion,
          nota,
          comentario: observaciones,
          fecha: new Date().toISOString()
        };
        
        // Llamar a la función onSubmit para que el componente padre pueda manejar la navegación
        onSubmit(datosEncuesta);
        
        // Recargar la página para actualizar el carrito
        if (Platform.OS === 'web') {
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      } else {
        console.error('❌ Error al enviar encuesta:', response.status);
        // Aún así, permitir continuar
        onSubmit({ valoracion, nota: obtenerNota(valoracion), comentario: observaciones });
        
        // Recargar la página para actualizar el carrito
        if (Platform.OS === 'web') {
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      }
    } catch (error) {
      console.error('❌ Error al enviar encuesta:', error);
      // Aún así, permitir continuar
      onSubmit({ valoracion, nota: obtenerNota(valoracion), comentario: comentario.trim() });
      
      // Recargar la página para actualizar el carrito
      if (Platform.OS === 'web') {
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } finally {
      setEnviando(false);
    }
  };

  const dynamicStyles = getDynamicStyles();

  // Prevenir scroll en el modal container
  const handleTouchMove = (e) => {
    // Solo prevenir si el toque es en el overlay, no en el contenido
    if (e.target === e.currentTarget) {
      e.preventDefault();
    }
  };

  const renderSurveyContent = () => (
    <ScrollView
      style={styles.scrollWrapper}
      contentContainerStyle={[
        styles.scrollContent,
        isMobile ? dynamicStyles.scrollContentMobile : dynamicStyles.scrollContentDesktop
      ]}
      showsVerticalScrollIndicator={!isMobile}
    >
      {/* Encabezado de éxito */}
      <View style={styles.successHeader}>
        <View style={[styles.successCircle, dynamicStyles.successCircle]}>
          <MaterialCommunityIcons 
            name="check-circle" 
            size={dynamicStyles.successIcon.fontSize} 
            color={colores.verde} 
          />
        </View>
        <Text style={[styles.successTitle, dynamicStyles.successTitle]}>
          ¡Pago realizado con éxito!
        </Text>
        <Text style={[styles.successSubtitle, dynamicStyles.successSubtitle]}>
          Tu compra fue confirmada correctamente.
        </Text>
      </View>

      {/* Separador visual */}
      <View style={[styles.divider, dynamicStyles.divider]} />

      {/* Encuesta de satisfacción */}
      <View style={styles.surveySection}>
        <Text style={[styles.surveyTitle, dynamicStyles.surveyTitle]}>
          ¿Cómo fue tu experiencia de compra?
        </Text>
        <Text style={[styles.surveySubtitle, dynamicStyles.surveySubtitle]}>
          Tu opinión nos ayuda a mejorar.
        </Text>

        <View style={[styles.caritasContainer, dynamicStyles.caritasContainer, { gap: isSmallMobile ? 5 : isMobile ? 8 : 12 }]}>
          {opcionesValoracion.map(opcion => (
            <TouchableOpacity 
              key={opcion.id}
              style={[
                styles.ratingCard, 
                dynamicStyles.ratingCard,
                valoracion === opcion.id && { 
                  borderColor: opcion.color, 
                  backgroundColor: `${opcion.color}15`,
                  ...Platform.select({
                    web: {
                      boxShadow: `0 2px 8px ${opcion.color}30`,
                    },
                    default: {},
                  })
                }
              ]}
              onPress={() => setValoracion(opcion.id)}
            >
              <MaterialCommunityIcons 
                name={opcion.icon}
                size={dynamicStyles.ratingIcon.fontSize} 
                color={valoracion === opcion.id ? opcion.color : colores.grisClaro} 
              />
              <Text style={[styles.ratingText, dynamicStyles.ratingText]}>
                {opcion.texto}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        <View style={styles.commentSection}>
          <View style={styles.commentLabelContainer}>
            <Text style={[styles.commentLabel, dynamicStyles.commentLabel]}>
              ¿Querés contarnos algo más?
            </Text>
            <Text style={styles.optionalText}>
              Opcional
            </Text>
          </View>
          <TextInput
            style={[styles.commentInput, dynamicStyles.commentInput]}
            multiline={true}
            numberOfLines={isMobile ? 3 : 4}
            placeholder="Escribí tu opinión…"
            value={comentario}
            onChangeText={setComentario}
            textAlignVertical="top"
            {...Platform.select({
              web: {
                outlineStyle: 'none',
              },
              default: {},
            })}
          />
        </View>

        <TouchableOpacity 
          style={[
            styles.submitButton, 
            dynamicStyles.submitButton, 
            (!valoracion || enviando) && styles.submitButtonDisabled
          ]}
          onPress={handleSubmit}
          disabled={!valoracion || enviando}
        >
          {enviando ? (
            <ActivityIndicator color={colores.blanco} />
          ) : (
            <>
              <MaterialCommunityIcons 
                name="send-outline" 
                size={18} 
                color={colores.blanco} 
              />
              <Text style={[
                styles.submitButtonText, 
                dynamicStyles.submitButtonText,
                (!valoracion || enviando) && styles.submitButtonTextDisabled
              ]}>
                Enviar opinión
              </Text>
            </>
          )}
        </TouchableOpacity>

        <TouchableOpacity style={[styles.skipButton, dynamicStyles.skipButton]} onPress={onClose}>
          <Text style={[styles.skipButtonText, dynamicStyles.skipButtonText]}>
            Omitir y finalizar
          </Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={onClose}
      {...(Platform.OS === 'web' ? {
        style: {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 999999,
        }
      } : {})}
    >
      <View 
        style={[styles.modalContainer, dynamicStyles.modalContainer]}
        className="modal-overlay"
        nativeID="modal-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
        onTouchMove={handleTouchMove}
      >
        <View 
          style={[styles.modalContent, dynamicStyles.modalContent]}
          {...(Platform.OS === 'web' ? { 'data-modal-content': 'true' } : {})}
          nativeID="modal-content"
        >
          {renderSurveyContent()}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    padding: 20,
    ...(Platform.OS === 'web' ? {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100vw',
      height: '100dvh',
      minHeight: '100vh',
      zIndex: 999999,
      display: 'flex',
      overflow: 'hidden',
      isolation: 'isolate',
    } : {
      flex: 1,
    }),
  },
  modalContent: {
    width: '90%',
    maxWidth: 600,
    backgroundColor: colores.blanco,
    borderRadius: 16,
    padding: 2,
    ...(Platform.OS === 'web'
      ? { 
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          zIndex: 1000001,
        }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.15,
          shadowRadius: 8,
          elevation: 8,
        }
    )
  },
  // Encabezado de éxito
  successHeader: {
    alignItems: 'center',
    paddingVertical: 6,
  },
  successCircle: {
    backgroundColor: '#e8f5e8',
    justifyContent: 'center',
    alignItems: 'center',
  },
  successTitle: {
    fontFamily: fuentes.bold,
    color: colores.verde,
    textAlign: 'center',
  },
  successSubtitle: {
    fontFamily: fuentes.semiBold,
    color: colores.textoPrincipal || '#333',
    textAlign: 'center',
  },
  successAuxText: {
    fontFamily: fuentes.regular,
    color: colores.textoSecundario || '#666',
    textAlign: 'center',
    paddingHorizontal: 20,
  },
  // Separador
  divider: {
    height: 1,
    backgroundColor: colores.grisClaro || '#e0e0e0',
    alignSelf: 'stretch',
  },
  // Sección de encuesta
  surveySection: {
    flex: 1,
  },
  surveyTitle: {
    fontFamily: fuentes.semiBold,
    color: colores.textoPrincipal || '#333',
    textAlign: 'center',
    marginBottom: 6,
  },
  surveySubtitle: {
    fontFamily: fuentes.regular,
    color: colores.textoSecundario || '#666',
    textAlign: 'center',
    marginBottom: 20,
  },
  caritasContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginVertical: 20,
  },
  // Tarjetas de valoración
  ratingCard: {
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    borderWidth: 2,
    borderColor: colores.grisClaro || '#e0e0e0',
    backgroundColor: colores.blanco,
  },
  ratingText: {
    fontFamily: fuentes.medium,
    color: colores.textoPrincipal || '#333',
    textAlign: 'center',
  },
  // Sección de comentario
  commentSection: {
    marginVertical: 10,
  },
  commentLabelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  commentLabel: {
    fontFamily: fuentes.semiBold,
    color: colores.textoPrincipal || '#333',
  },
  optionalText: {
    fontFamily: fuentes.regular,
    color: colores.textoSecundario || '#666',
    fontSize: 12,
    marginLeft: 6,
  },
  commentInput: {
    borderWidth: 1,
    borderColor: colores.grisClaro || '#e0e0e0',
    backgroundColor: colores.blanco,
    fontFamily: fuentes.regular,
    color: colores.textoPrincipal || '#333',
    textAlignVertical: 'top',
    ...(Platform.OS === 'web' ? {
      ':focus': {
        borderColor: colores.verde,
        borderWidth: 2,
      },
    } : {}),
  },
  // Botones
  submitButton: {
    backgroundColor: colores.verde,
    borderRadius: 10,
    alignItems: 'center',
  },
  submitButtonDisabled: {
    backgroundColor: '#a8e6a3',
    opacity: 0.7,
  },
  submitButtonText: {
    color: colores.blanco,
    fontFamily: fuentes.semiBold,
  },
  submitButtonTextDisabled: {
    color: colores.blanco,
  },
  skipButton: {
    alignItems: 'center',
  },
  skipButtonText: {
    color: colores.textoSecundario || '#666',
    fontFamily: fuentes.regular,
  },
  // Scroll
  scrollWrapper: {
    width: '100%',
  },
  scrollContent: {
    width: '100%',
  },
});

export default EncuestaFinalizar;
